import clsx from "clsx";
import { NavLink, useMatch, useResolvedPath } from "react-router-dom";
import { NavBadgeGroup } from "@/components/NavBadge";
import { ViewportEdgeNav } from "@/components/ViewportEdgeNav";
import { useNavBadges } from "@/hooks/useNavBadges";
import { useCurrentUser } from "@/lib/auth";
import type { NavBadgeKey } from "@/lib/navBadges";
import type { NavBadgeCounts } from "@/lib/navBadges";

export interface SectionSidebarNavLinkItem {
  key: string;
  label: string;
  href: string;
  /** Exact match only (z. B. Index `/dokumente` nicht für Unterpfade). */
  end?: boolean;
  /** Externer Link statt React-Router. */
  external?: boolean;
  /** Überschreibt die automatische Active-Erkennung von NavLink. */
  active?: boolean;
  badgeKeys?: NavBadgeKey[];
  badgeSumKeys?: NavBadgeKey[];
  /** Unterpunkte (3. Ebene) im selben vertikalen Menü. */
  children?: SectionSidebarNavItem[];
  /** Unterpunkte anzeigen (Default: aktiv wenn Parent aktiv). */
  childrenOpen?: boolean;
}

export interface SectionSidebarNavButtonItem {
  key: string;
  label: string;
  active: boolean;
  onClick: () => void;
  /** Ziel für eine Aufgaben-Führung (`data-tour`). */
  tourTarget?: string;
  badgeKeys?: NavBadgeKey[];
  badgeSumKeys?: NavBadgeKey[];
  children?: SectionSidebarNavItem[];
  childrenOpen?: boolean;
}

export type SectionSidebarNavItem = SectionSidebarNavLinkItem | SectionSidebarNavButtonItem;

function isLinkItem(item: SectionSidebarNavItem): item is SectionSidebarNavLinkItem {
  return "href" in item;
}

function itemIsActive(item: SectionSidebarNavItem, linkActive = false): boolean {
  if (isLinkItem(item)) {
    return typeof item.active === "boolean" ? item.active : linkActive;
  }
  return item.active;
}

function childrenAreOpen(item: SectionSidebarNavItem, parentActive: boolean): boolean {
  if (!item.children?.length) return false;
  if (typeof item.childrenOpen === "boolean") return item.childrenOpen;
  return parentActive || item.children.some((child) => itemIsActive(child));
}

function useLinkActive(href: string, end?: boolean, override?: boolean): boolean {
  const resolved = useResolvedPath(href);
  const match = useMatch({ path: resolved.pathname, end: end ?? false });
  if (typeof override === "boolean") return override;
  return match != null;
}

export function SectionSidebarNav({
  items,
  ariaLabel,
}: {
  items: SectionSidebarNavItem[];
  ariaLabel: string;
}) {
  const { user } = useCurrentUser();
  const badgeCounts = useNavBadges(user);

  return (
    <ViewportEdgeNav>
      <nav className="eh-project-detail-nav" aria-label={ariaLabel}>
        <ul className="eh-project-detail-nav__list" role="list">
          {items.map((item) => (
            <SectionSidebarNavEntry key={item.key} item={item} badgeCounts={badgeCounts} />
          ))}
        </ul>
      </nav>
    </ViewportEdgeNav>
  );
}

function SectionSidebarNavEntry({
  item,
  badgeCounts,
  nested = false,
}: {
  item: SectionSidebarNavItem;
  badgeCounts: NavBadgeCounts;
  nested?: boolean;
}) {
  if (isLinkItem(item)) {
    return <SectionSidebarNavLink entry={item} badgeCounts={badgeCounts} nested={nested} />;
  }
  return <SectionSidebarNavButton entry={item} badgeCounts={badgeCounts} nested={nested} />;
}

function SectionSidebarNavLink({
  entry,
  badgeCounts,
  nested,
}: {
  entry: SectionSidebarNavLinkItem;
  badgeCounts: NavBadgeCounts;
  nested: boolean;
}) {
  const active = useLinkActive(entry.href, entry.end, entry.active);
  const label = (
    <>
      <span className="eh-project-detail-nav__label">{entry.label}</span>
      <NavBadgeGroup
        badgeKeys={entry.badgeKeys}
        badgeSumKeys={entry.badgeSumKeys}
        counts={badgeCounts}
      />
    </>
  );
  const className = clsx(
    "eh-project-detail-nav__item",
    nested && "eh-project-detail-nav__item--child",
    active && "eh-project-detail-nav__item--active",
  );

  return (
    <li>
      {entry.external ? (
        <a href={entry.href} className={className} aria-current={active ? "page" : undefined}>
          {label}
        </a>
      ) : (
        <NavLink to={entry.href} end={entry.end} className={className} aria-current={active ? "page" : undefined}>
          {label}
        </NavLink>
      )}
      <SectionSidebarNavChildren item={entry} parentActive={active} badgeCounts={badgeCounts} />
    </li>
  );
}

function SectionSidebarNavButton({
  entry,
  badgeCounts,
  nested,
}: {
  entry: SectionSidebarNavButtonItem;
  badgeCounts: NavBadgeCounts;
  nested: boolean;
}) {
  const active = entry.active;
  return (
    <li>
      <button
        type="button"
        data-tour={entry.tourTarget}
        className={clsx(
          "eh-project-detail-nav__item",
          nested && "eh-project-detail-nav__item--child",
          active && "eh-project-detail-nav__item--active",
        )}
        aria-current={active ? "page" : undefined}
        onClick={entry.onClick}
      >
        <span className="eh-project-detail-nav__label">{entry.label}</span>
        <NavBadgeGroup
          badgeKeys={entry.badgeKeys}
          badgeSumKeys={entry.badgeSumKeys}
          counts={badgeCounts}
        />
      </button>
      <SectionSidebarNavChildren item={entry} parentActive={active} badgeCounts={badgeCounts} />
    </li>
  );
}

function SectionSidebarNavChildren({
  item,
  parentActive,
  badgeCounts,
}: {
  item: SectionSidebarNavItem;
  parentActive: boolean;
  badgeCounts: NavBadgeCounts;
}) {
  if (!childrenAreOpen(item, parentActive) || !item.children?.length) return null;

  return (
    <ul className="eh-project-detail-nav__children" role="list">
      {item.children.map((child) => (
        <SectionSidebarNavEntry key={child.key} item={child} badgeCounts={badgeCounts} nested />
      ))}
    </ul>
  );
}
